/**
 * Spotlight Component
 * Creates a visual highlight around the target element using SVG mask
 */

import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface SpotlightProps {
  /** CSS selector or element to highlight */
  targetSelector: string;
  /** Padding around the highlighted element (in pixels) */
  padding?: number;
}

interface ElementBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function Spotlight({ targetSelector, padding = 8 }: SpotlightProps) {
  const [bounds, setBounds] = useState<ElementBounds | null>(null);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10; // Try up to 10 times over 1 second

    const updateBounds = () => {
      try {
        const element = document.querySelector(targetSelector);

        if (!element) {
          // Retry if element not found and we haven't exceeded max retries
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(updateBounds, 100);
            return;
          }

          console.warn(`Tutorial: Target element "${targetSelector}" not found after ${maxRetries} retries`);
          setBounds(null);
          return;
        }

        const rect = element.getBoundingClientRect();

        // Check if element has valid dimensions (is actually rendered)
        if (rect.width === 0 || rect.height === 0) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(updateBounds, 100);
            return;
          }
        }

        setBounds({
          x: rect.left - padding,
          y: rect.top - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
      } catch (error) {
        console.error("Tutorial: Error calculating element bounds:", error);
        setBounds(null);
      }
    };

    // Initial calculation with delay
    const initialTimeout = setTimeout(updateBounds, 300);

    // Recalculate on window resize or scroll
    window.addEventListener("resize", updateBounds);
    window.addEventListener("scroll", updateBounds, true); // Capture phase to catch all scrolls

    return () => {
      window.removeEventListener("resize", updateBounds);
      window.removeEventListener("scroll", updateBounds, true);
      clearTimeout(initialTimeout);
    };
  }, [targetSelector, padding]);

  if (!bounds) {
    return null;
  }

  return (
    <svg
      className="pointer-events-none fixed inset-0 z-[101]"
      style={{ width: "100%", height: "100%" }}
    >
      <defs>
        <mask id="spotlight-mask">
          {/* White fills the entire screen (visible) */}
          <rect x="0" y="0" width="100%" height="100%" fill="white" />

          {/* Black rectangle cuts out the spotlight area (transparent) */}
          <motion.rect
            x={bounds.x}
            y={bounds.y}
            width={bounds.width}
            height={bounds.height}
            rx="8"
            ry="8"
            fill="black"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </mask>
      </defs>

      {/* Dark overlay with spotlight cutout */}
      <motion.rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="rgba(0, 0, 0, 0.6)"
        mask="url(#spotlight-mask)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />

      {/* Optional: Highlight border around the spotlight */}
      <motion.rect
        x={bounds.x}
        y={bounds.y}
        width={bounds.width}
        height={bounds.height}
        rx="8"
        ry="8"
        fill="none"
        stroke="rgba(59, 130, 246, 0.5)"
        strokeWidth="2"
        className="pointer-events-none"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      />
    </svg>
  );
}
